import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ShieldCheck, Twitter, Linkedin, Youtube, Github, ArrowUpRight, CalendarCheck, Mail, ArrowRight, Check, X, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useDemo } from "@/components/landing/DemoContext";
import { BASES, ADDONS } from "@/config/clinicOS";
import { DISCIPLINES } from "@/config/disciplines";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY_LINKS = [
  { label: "Why ClinicFirst", to: "/why-clinicfirst" },
  { label: "Join Clinic OS", to: "/join-clinicos" },
  { label: "KAI", to: "/kai" },
  { label: "Integrations", to: "/integrations" },
  { label: "Book a Demo", to: "/book-a-demo" },
];

const TRUST = [
  { label: "HIPAA-ready",  icon: ShieldCheck, color: "text-emerald-500" },
  { label: "SOC 2 aligned",                    color: "text-blue-500" },
  { label: "99.9% uptime",                     color: "text-violet-500" },
  { label: "Built in Canada",                  color: "text-rose-500" },
];

const SOCIALS = [
  { label: "LinkedIn", icon: Linkedin, href: "https://www.linkedin.com/", hover: "hover:text-[#0A66C2] hover:border-[#0A66C2]/40 hover:bg-[#0A66C2]/8" },
  { label: "Twitter",  icon: Twitter,  href: "https://twitter.com/",      hover: "hover:text-sky-500 hover:border-sky-500/40 hover:bg-sky-500/10" },
  { label: "YouTube",  icon: Youtube,  href: "https://youtube.com/",      hover: "hover:text-rose-500 hover:border-rose-500/40 hover:bg-rose-500/10" },
  { label: "GitHub",   icon: Github,   href: "https://github.com/",       hover: "hover:text-foreground hover:border-foreground/40 hover:bg-foreground/8" },
];

/* ---------- Newsletter modal ---------- */
const NewsletterModal = ({ open, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("loading");
    setError("");
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim() || undefined,
        source: "footer-modal",
      });
      setState("success");
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.detail?.[0]?.msg || "Please enter a valid email.");
    }
  };

  return (
    <div
      data-testid="newsletter-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
      >
        {/* Colored top accent */}
        <div className="h-[3px] w-full bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500" />
        {/* Decorative glow */}
        <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-violet-500/20 blur-3xl" />

        <button
          type="button"
          onClick={onClose}
          data-testid="newsletter-modal-close"
          aria-label="Close"
          className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative p-6 sm:p-7">
          {state === "success" ? (
            <div className="text-center" data-testid="newsletter-modal-success">
              <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/30">
                <Check className="h-6 w-6" />
              </span>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight">You&apos;re on the list.</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Watch your inbox for the next product update — usually first week of the month.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-5 inline-flex h-10 items-center gap-1.5 rounded-xl bg-foreground px-5 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 text-white shadow-sm shadow-violet-500/30">
                  <Sparkles className="h-[18px] w-[18px]" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-extrabold tracking-tight sm:text-xl">Subscribe to product updates</h3>
                  <p className="text-[12.5px] text-muted-foreground">Monthly. New modules, AI updates, no spam.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="mt-5 space-y-3" data-testid="newsletter-modal-form">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Name <span className="font-normal lowercase tracking-normal text-muted-foreground/60">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={state === "loading"}
                    data-testid="newsletter-modal-name"
                    className="mt-1.5 block w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-[13.5px] font-medium text-foreground placeholder:text-muted-foreground/60 focus:border-violet-500/55 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="you@clinic.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={state === "loading"}
                    data-testid="newsletter-modal-email"
                    className="mt-1.5 block w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-[13.5px] font-medium text-foreground placeholder:text-muted-foreground/60 focus:border-violet-500/55 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                  />
                </div>

                {state === "error" && (
                  <p data-testid="newsletter-modal-error" className="text-[12px] font-medium text-rose-500">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={state === "loading"}
                  data-testid="newsletter-modal-submit"
                  className="mt-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-sm font-bold text-white shadow-md shadow-violet-600/25 transition-all hover:-translate-y-0.5 hover:from-blue-500 hover:to-violet-500 hover:shadow-lg disabled:opacity-60"
                >
                  {state === "loading" ? "Subscribing…" : (
                    <>
                      <Mail className="h-4 w-4" />
                      Subscribe
                      <ArrowRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </button>

                <p className="pt-1 text-center text-[11px] text-muted-foreground">
                  Unsubscribe anytime · We never share your email.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const Footer = () => {
  const openDemo = useDemo();
  const year = new Date().getFullYear();
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  return (
    <footer data-testid="site-footer" className="relative border-t border-border bg-gradient-to-b from-card/40 via-card/60 to-card/30">
      {/* Top colored accent bar */}
      <div className="h-[3px] w-full bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500" />

      {/* Decorative soft glows */}
      <div className="pointer-events-none absolute inset-0 -z-0 overflow-hidden">
        <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-blue-500/8 blur-3xl" />
        <div className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-violet-500/8 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-emerald-500/6 blur-3xl" />
      </div>

      {/* Top CTA strip — headline + Book a Demo + Subscribe */}
      <div className="relative border-b border-border/60">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 px-6 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-12">
          <div>
            <h3 className="font-display text-xl font-extrabold tracking-tight sm:text-2xl">
              Run your clinic on{" "}
              <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                Clinic OS.
              </span>
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              One AI-native platform. Starts at $297/mo.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              onClick={() => setNewsletterOpen(true)}
              data-testid="footer-subscribe-btn"
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-violet-500/35 bg-violet-500/[0.06] px-5 text-sm font-semibold text-foreground transition-all hover:-translate-y-0.5 hover:border-violet-500/60 hover:bg-violet-500/[0.10] hover:shadow-md hover:shadow-violet-500/15"
            >
              <Mail className="h-4 w-4 text-violet-500" />
              Subscribe to updates
            </button>
            <button
              data-testid="footer-book-demo"
              onClick={openDemo}
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-6 text-sm font-semibold text-white shadow-md shadow-violet-600/25 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <CalendarCheck className="h-4 w-4" />
              Book a Demo
            </button>
          </div>
        </div>
      </div>

      {/* Main link grid */}
      <div className="relative mx-auto max-w-7xl px-6 py-14 sm:px-8 lg:px-12">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-2.5">
              <Logo />
              <span className="font-display text-lg font-extrabold tracking-tight">
                Clinic <span className="text-gradient">OS</span>
              </span>
              <span className="h-3 w-px bg-border" aria-hidden />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                by ClinicFirst
              </span>
            </Link>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
              The all-in-one, AI-powered operating system clinics run their entire business on —
              one inbox, one calendar, one patient record.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-secondary/40 text-muted-foreground transition-all hover:-translate-y-0.5 ${s.hover}`}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link columns — 2x2 on mobile, 4-col strip on lg */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:gap-x-8 lg:col-span-8 lg:grid-cols-4">
            {/* Bases */}
            <div>
            <h4 className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              Bases
            </h4>
            <ul className="mt-4 space-y-2.5">
              {BASES.map((b) => (
                <li key={b.id}>
                  <Link
                    to={`/products/${b.id}`}
                    data-testid={`footer-base-${b.id}`}
                    className="inline-flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-foreground"
                  >
                    {b.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/platform" className="inline-flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-foreground">
                  The Platform
                </Link>
              </li>
              <li>
                <Link to="/kai-experience" className="inline-flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-foreground">
                  Try KAI
                </Link>
              </li>
            </ul>
          </div>

          {/* Add-ons */}
          <div>
            <h4 className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              Add-ons
            </h4>
            <ul className="mt-4 space-y-2.5">
              {ADDONS.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/products/${a.id}`}
                    data-testid={`footer-addon-${a.id}`}
                    className="inline-flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-foreground"
                  >
                    {a.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Built for */}
          <div>
            <h4 className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Built for
            </h4>
            <ul className="mt-4 space-y-2.5">
              {DISCIPLINES.map((d) => (
                <li key={d.slug}>
                  <Link
                    to={`/disciplines/${d.slug}`}
                    data-testid={`footer-discipline-${d.slug}`}
                    className="inline-flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-foreground"
                  >
                    {d.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Company
            </h4>
            <ul className="mt-4 space-y-2.5">
              {COMPANY_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="inline-flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-foreground"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="mailto:hello@clinicfirst.app"
                  className="inline-flex items-center gap-1 text-sm text-foreground/80 transition-colors hover:text-foreground"
                >
                  hello@clinicfirst.app <ArrowUpRight className="h-3 w-3" />
                </a>
              </li>
            </ul>
          </div>
          </div>
        </div>

        {/* Trust badges row */}
        <div className="mt-12 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border/60 pt-6 text-xs text-muted-foreground">
          <span className="font-semibold uppercase tracking-[0.18em] text-foreground/70">Trust</span>
          <span className="h-1 w-1 rounded-full bg-border" aria-hidden />
          {TRUST.map((t, i) => {
            const Icon = t.icon;
            return (
              <span key={t.label} className="inline-flex items-center gap-1.5">
                {Icon ? (
                  <Icon className={`h-3.5 w-3.5 ${t.color}`} />
                ) : (
                  <span className={`h-1.5 w-1.5 rounded-full ${t.color.replace("text-", "bg-")}`} />
                )}
                <span className="text-foreground/75">{t.label}</span>
                {i < TRUST.length - 1 && <span className="ml-2 h-1 w-1 rounded-full bg-border" aria-hidden />}
              </span>
            );
          })}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-border/60">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 px-6 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-12">
          <p>
            © {year} ClinicFirst Inc. · Clinic OS™ is a product of ClinicFirst. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/privacy" className="transition-colors hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="transition-colors hover:text-foreground">Terms</Link>
            <Link to="/security" className="transition-colors hover:text-foreground">Security</Link>
            <Link to="/status" className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              All systems operational
            </Link>
          </div>
        </div>
      </div>

      <NewsletterModal open={newsletterOpen} onClose={() => setNewsletterOpen(false)} />
    </footer>
  );
};
